// Property Guide: "how to do it" playbook per property

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";

export type PropertyGuideCategory =
  | "setup_standard"
  | "cleaning_standard"
  | "staging_standard"
  | "inventory_restocking"
  | "access_lockbox_parking"
  | "other";

export interface PropertyGuideEntry {
  id: string;
  property_id: string;
  title: string;
  room: string | null;
  category: PropertyGuideCategory;
  instructions: string | null;
  photo_urls: string[];
  video_url: string | null;
  pinned: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

const CATEGORY_LABELS: Record<PropertyGuideCategory, string> = {
  setup_standard: "Setup standard",
  cleaning_standard: "Cleaning standard",
  staging_standard: "Staging standard",
  inventory_restocking: "Inventory / restocking guide",
  access_lockbox_parking: "Access / lockbox / parking",
  other: "Other",
};

export const PROPERTY_GUIDE_CATEGORIES = Object.entries(CATEGORY_LABELS).map(
  ([value, label]) => ({ value: value as PropertyGuideCategory, label })
);

export function usePropertyGuideEntries(propertyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.propertyGuide.byProperty(propertyId ?? ""),
    queryFn: async () => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("property_guide_entries")
        .select("*")
        .eq("property_id", propertyId)
        .order("pinned", { ascending: false })
        .order("display_order", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as PropertyGuideEntry[];
    },
    enabled: !!propertyId,
  });
}

export function useCreatePropertyGuideEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (entry: {
      property_id: string;
      title: string;
      room?: string | null;
      category: PropertyGuideCategory;
      instructions?: string | null;
      photo_urls?: string[];
      video_url?: string | null;
      pinned?: boolean;
      display_order?: number;
    }) => {
      const { data, error } = await supabase
        .from("property_guide_entries")
        .insert({
          property_id: entry.property_id,
          title: entry.title,
          room: entry.room ?? null,
          category: entry.category,
          instructions: entry.instructions ?? null,
          photo_urls: entry.photo_urls ?? [],
          video_url: entry.video_url ?? null,
          pinned: entry.pinned ?? false,
          display_order: entry.display_order ?? 0,
        })
        .select()
        .single();
      if (error) throw error;
      return data as PropertyGuideEntry;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.propertyGuide.byProperty(variables.property_id),
      });
    },
  });
}

export function useUpdatePropertyGuideEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (entry: {
      id: string;
      property_id: string;
      title?: string;
      room?: string | null;
      category?: PropertyGuideCategory;
      instructions?: string | null;
      photo_urls?: string[];
      video_url?: string | null;
      pinned?: boolean;
      display_order?: number;
    }) => {
      const { id, property_id, ...updates } = entry;
      const { data, error } = await supabase
        .from("property_guide_entries")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as PropertyGuideEntry;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.propertyGuide.byProperty(variables.property_id),
      });
    },
  });
}

export function useDeletePropertyGuideEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, property_id }: { id: string; property_id: string }) => {
      const { error } = await supabase
        .from("property_guide_entries")
        .delete()
        .eq("id", id);
      if (error) throw error;
      return { property_id };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.propertyGuide.byProperty(data.property_id),
      });
    },
  });
}

export function getCategoryLabel(category: PropertyGuideCategory): string {
  return CATEGORY_LABELS[category] ?? category;
}
